import React, { useEffect, useMemo } from "react";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import type { IncidentTypeValue } from "@whatslly/types/messages-server/destinations";
import styles from "../modals/Modals.module.scss";
import { useDependencyInjector } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import upperFirst from "lodash/upperFirst";
import { observer } from "mobx-react-lite";
import { useField } from "formik";

type IncidentTypeOption = { label: string; value: IncidentTypeValue };

export const IncidentTypesSelect: React.FC<{ name: string }> = observer(({ name }) => {
  const { t } = useTranslation();
  const destinationsStore = useDependencyInjector(DestinationsStore);
  const [field, , helpers] = useField<IncidentTypeValue[] | undefined>(name);

  const options = useMemo<IncidentTypeOption[]>(
    () =>
      destinationsStore.supportedIncidentTypes.map((type) => ({
        label: upperFirst(t(`destinations_page.incident_types.${type}`)),
        value: type,
      })),
    [destinationsStore.supportedIncidentTypes, t]
  );

  const value = useMemo(() => options.filter((option) => field.value?.includes(option.value)), [field.value, options]);

  useEffect(() => {
    if (options.length > 0 && (!field.value?.length || field.value.length !== value.length)) {
      helpers.setValue((value.length > 0 ? value : options.slice(0, 1)).map((option) => option.value));
    }
  }, [field.value, helpers, options, value]);

  const handleChange = (selected: IncidentTypeOption[]) => {
    helpers.setValue((selected.length > 0 ? selected : options.slice(0, 1)).map((option) => option.value));
  };

  return (
    <div className={styles.field}>
      <Label className={styles.filedLabel} text={t("destinations_page.incident_types.label")} required />
      <Select<IncidentTypeOption, true>
        ignoreMenuDirection
        isSearchable
        name={name}
        paddingInline="none"
        options={options}
        isClearable={false}
        isMulti
        placeholder={t("destinations_page.all_types")}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
});
